import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useReducer } from 'react';
import { cognitoConfig } from 'config';
import { PATH_AUTH } from 'routes/paths';
import axios from 'utils/axios';
import { clearDemoSession } from 'utils/demoEnvironment';

const CUSTOMER_MAP = {
  '35ef53e2-0e6a-4b2d-b5d9-53468f9c7e38': 'Adisebaba',
  '4c5a527f-8db0-4d1a-86f9-c7d96d1bf37d': 'Gamester',
  'b0000001-0001-4001-8001-000000000001': 'Mobeat',
};

export const UserPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.clientId,
});

/**
 * The signed-in user's ID token as an Authorization header, refreshed when it has expired; null
 * when nobody is signed in.
 *
 * The ID token lasts an hour, and getSession below only puts it on the shared header when the app
 * loads — so a tab left open started answering "Invalid or expired token" until a reload. Cognito's
 * getSession hands back the cached session while it is valid and swaps it with the refresh token
 * once it is not, so asking on every request costs nothing until the hour is up.
 */
export const freshAuthorization = () =>
  new Promise((resolve) => {
    const user = UserPool.getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err, session) => {
      resolve(err || !session?.isValid() ? null : `Bearer ${session.getIdToken().getJwtToken()}`);
    });
  });

axios.interceptors.request.use(async (config) => {
  const authorization = await freshAuthorization();
  if (authorization) {
    // The default too: clients that copy it (monetization/push/api.js) would otherwise keep the
    // expired one.
    axios.defaults.headers.common.Authorization = authorization;
    config.headers.Authorization = authorization;
  }
  return config;
});

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  AUTHENTICATE: (state, action) => {
    const { isAuthenticated, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      app: null,
    };
  },
  APP_INITIALIZE: (state, action) => ({
    ...state,
    app: action.payload,
  }),
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'cognito',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  initializeApp: () => Promise.resolve(),
  updateProfile: () => Promise.resolve(),
  changePassword: () => Promise.resolve(),
});

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUserAttributes = useCallback(
    (currentUser) =>
      new Promise((resolve, reject) => {
        currentUser.getUserAttributes((err, attributes) => {
          if (err) {
            dispatch({
              type: 'AUTHENTICATE',
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });

            reject(err);
          } else {
            const results = {};

            attributes.forEach((attribute) => {
              results[attribute.Name.replace('custom:', '')] = attribute.Value;
            });
            resolve(results);
          }
        });
      }),
    [],
  );

  const getSession = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const user = UserPool.getCurrentUser();
        if (user) {
          user.getSession(async (err, session) => {
            if (err) {
              reject(err);
            } else {
              const attributes = await getUserAttributes(user);
              const token = session.getIdToken().getJwtToken();
              axios.defaults.headers.common.Authorization = `Bearer ${token}`;

              let mergedUser = { ...attributes };
              if (attributes.customerId && attributes.email) {
                try {
                  const users = await axios.get(`/customers/${attributes.customerId}/users`);
                  const row = Array.isArray(users)
                    ? users.find((u) => u.email?.toLowerCase() === attributes.email?.toLowerCase())
                    : null;
                  if (row?.organizationRole) {
                    mergedUser.organizationRole = row.organizationRole;
                  }
                  if (row?.applicationScope != null) {
                    mergedUser.applicationScope = row.applicationScope;
                  }
                } catch {
                  /* server unreachable — fall back below */
                }
              }
              if (!mergedUser.organizationRole) {
                mergedUser.organizationRole =
                  attributes.customerId === 'b0000001-0001-4001-8001-000000000001' ? 'mobeat_staff' : 'customer_admin';
              }
              if (mergedUser.applicationScope == null) {
                mergedUser.applicationScope = { type: 'all' };
              }

              dispatch({
                type: 'AUTHENTICATE',
                payload: { isAuthenticated: true, user: mergedUser },
              });

              resolve({
                user,
                session,
                headers: { Authorization: token },
              });
            }
          });
        } else {
          dispatch({
            type: 'AUTHENTICATE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }),
    [getUserAttributes],
  );

  const initial = useCallback(async () => {
    try {
      await getSession();
    } catch {
      dispatch({
        type: 'AUTHENTICATE',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [getSession]);

  useEffect(() => {
    initial();
  }, [initial]);

  // We make sure to handle the user update here, but return the resolve value in order for our components to be
  // able to chain additional `.then()` logic. Additionally, we `.catch` the error and "enhance it" by providing
  // a message that our React components can use.
  const login = useCallback(
    (email, password) =>
      new Promise((resolve, reject) => {
        const user = new CognitoUser({
          Username: email,
          Pool: UserPool,
        });

        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        user.authenticateUser(authDetails, {
          onSuccess: (data) => {
            getSession();
            resolve(data);
          },
          onFailure: (err) => {
            reject(err);
          },
          newPasswordRequired: () => {
            // Handle this on login page for update password.
            resolve({ message: 'newPasswordRequired' });
          },
        });
      }),
    [getSession],
  );

  // same thing here
  const logout = () => {
    const user = UserPool.getCurrentUser();
    if (user) {
      user.signOut();
      delete axios.defaults.headers.common.Authorization;
      clearDemoSession();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const register = (email, password, firstName, lastName) =>
    new Promise((resolve, reject) =>
      UserPool.signUp(
        email,
        password,
        [
          { Name: 'email', Value: email },
          { Name: 'name', Value: `${firstName} ${lastName}` },
        ],
        null,
        async (err) => {
          if (err) {
            reject(err);
            return;
          }
          resolve();
          window.location.href = PATH_AUTH.login;
        },
      ),
    );

  const resetPassword = () => {};

  const initializeApp = (app) => {
    dispatch({ type: 'APP_INITIALIZE', payload: app });
  };

  const updateProfile = useCallback(
    async (values = {}) => {
      const displayName = values.displayName ?? values.name;
      if (typeof displayName !== 'string' || !displayName.trim()) {
        throw new Error('Name is required');
      }
      const cognitoUser = UserPool.getCurrentUser();
      if (!cognitoUser) {
        throw new Error('Not authenticated');
      }
      await new Promise((resolve, reject) => {
        cognitoUser.updateAttributes([{ Name: 'name', Value: displayName.trim() }], (err, result) => {
          if (err) reject(err);
          else resolve(result);
        });
      });
      await getSession();
    },
    [getSession],
  );

  const changePassword = useCallback(
    (oldPassword, newPassword) =>
      new Promise((resolve, reject) => {
        const cognitoUser = UserPool.getCurrentUser();
        if (!cognitoUser) {
          reject(new Error('Not authenticated'));
          return;
        }
        cognitoUser.getSession((err) => {
          if (err) {
            reject(err);
            return;
          }
          cognitoUser.changePassword(oldPassword, newPassword, (err2, result) => {
            if (err2) reject(err2);
            else resolve(result);
          });
        });
      }),
    [],
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'cognito',
        user: {
          displayName: state?.user?.name,
          role: CUSTOMER_MAP[state?.user?.customerId] || 'User',
          ...state.user,
        },
        login,
        register,
        logout,
        resetPassword,
        initializeApp,
        updateProfile,
        changePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
